import { FC, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Button,
  IconButton,
  InlineNotification,
  RadioButton,
  Search,
  SkeletonText,
} from '@carbon/react';
import { Close, Edit } from '@carbon/icons-react';
import { useWellVersionsAtom } from 'components/well-page-content/atoms';
import { parseLocalDateTime } from 'utils/dates';
import type { WellsResponse, WellVersionsResponse } from 'types/api-responses';
import styles from './version-drawer.module.scss';

/**
 * The window a version was the live record for, as the drawer states it.
 * @param {WellVersionsResponse} version The version.
 * @return {string} The "Active: from – to" line.
 */
const activeRange = (version: WellVersionsResponse): string => {
  const from = parseLocalDateTime(version.beginTime, true);
  // The current version has no end: it is in force now, which reads better as
  // "present" than as a blank.
  const to = version.endTime
    ? parseLocalDateTime(version.endTime, true)
    : 'present';

  return `Active: ${from} – ${to}`;
};

const isCurrent = (version: WellVersionsResponse): boolean => {
  return version.versionId === 'current';
};

const versionLabel = (version: WellVersionsResponse): string => {
  return isCurrent(version)
    ? `${version.versionName} (Current)`
    : version.versionName;
};

export interface WellVersions {
  rows: WellVersionsResponse[];
  loading: boolean;
  failed: boolean;
  /** The version on screen, for the rail's line. */
  showingLabel: string;
}

/**
 * This well's versions, read once.
 *
 * Owned by the host because both the row and the drawer need it, and
 * `useWellVersionsAtom` builds a fresh atom per caller, so a hook in each would fetch
 * the same list twice.
 * @param {WellsResponse | undefined} well The subject well.
 * @param {string | undefined} versionId The version the page is showing.
 * @return {WellVersions} The list, its state, and the current label.
 */
export const useWellVersions = (
  well: WellsResponse | undefined,
  versionId?: string
): WellVersions => {
  const versions = useAtomValue(
    useWellVersionsAtom(well ? `${well.resources.versions.href}` : '')
  );

  return useMemo(() => {
    const failed = Boolean(versions.data && 'error' in versions.data);
    const rows: WellVersionsResponse[] =
      versions.data && !('error' in versions.data) ? versions.data : [];
    const showing = rows.find(
      (row) => row.versionId === (versionId ?? well?.versionId)
    );

    return {
      rows,
      loading: versions.loading,
      failed,
      showingLabel: showing ? versionLabel(showing) : '—',
    };
  }, [versions, versionId, well?.versionId]);
};

/**
 * The rail's version line: which version is on screen, and a way to change it.
 *
 * Mock v13 puts this at the top of Layout settings rather than the page header, since
 * the version is part of how the page is read. The drawer it opens belongs to the page
 * shell, not here, so it lands on the right of the whole page.
 * @param {object} props The label, whether it is still loading, and the open handler.
 * @return {JSX.Element} The line.
 */
export const WellVersionRow: FC<{
  label: string;
  loading: boolean;
  onOpen: () => void;
}> = ({ label, loading, onOpen }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>Version:</span>
    {loading ? (
      <SkeletonText width="80px" />
    ) : (
      <span className={styles.rowValue}>{label}</span>
    )}
    <IconButton
      kind="ghost"
      size="sm"
      align="bottom-right"
      label="Change version"
      onClick={onOpen}
    >
      <Edit />
    </IconButton>
  </div>
);

/**
 * The Well Versions drawer: every version of this well, with the window each was live
 * for, and a radio to pick one.
 *
 * Staged rather than immediate — the pick only takes effect on Apply — because changing
 * version refetches every card on the page, so a mis-click should cost nothing.
 * @param {object} props The versions, the current pick, and the handlers.
 * @return {JSX.Element} The drawer.
 */
export const WellVersionsDrawer: FC<{
  versions: WellVersionsResponse[];
  failed: boolean;
  loading: boolean;
  selectedId?: string;
  onClose: () => void;
  onApply: (versionId: string) => void;
}> = ({ versions, failed, loading, selectedId, onClose, onApply }) => {
  const [search, setSearch] = useState('');
  const [staged, setStaged] = useState<string | undefined>(selectedId);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return versions;

    // Matches the name and the dates, so "2026" or "March" finds a version the reader
    // remembers by when it was live rather than by its number.
    return versions.filter((version) =>
      `${versionLabel(version)} ${activeRange(version)}`
        .toLowerCase()
        .includes(term)
    );
  }, [versions, search]);

  return (
    <aside className={styles.drawer} aria-label="Well versions">
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Well Versions</h3>
          <p className={styles.subtitle}>
            Select a version to view for this well.
          </p>
        </div>
        <IconButton
          kind="ghost"
          size="sm"
          align="bottom-left"
          label="Close"
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </div>

      <div className={styles.body}>
        {failed && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle="Could not load this well's versions."
            lowContrast
            hideCloseButton
          />
        )}

        <Search
          size="sm"
          labelText="Search versions"
          placeholder="Search versions..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
        />

        {loading && <SkeletonText paragraph lineCount={4} />}

        {!loading && !failed && filtered.length === 0 && (
          <p className={styles.empty}>
            {versions.length === 0
              ? 'This well has only its current version.'
              : 'No versions match.'}
          </p>
        )}

        {filtered.map((version) => (
          <div
            key={version.versionId}
            className={
              version.versionId === staged
                ? `${styles.version} ${styles.versionSelected}`
                : styles.version
            }
          >
            <div className={styles.versionText}>
              <span className={styles.versionName}>
                {versionLabel(version)}
              </span>
              <span className={styles.versionRange}>
                {activeRange(version)}
              </span>
            </div>
            <RadioButton
              name="well-version"
              value={version.versionId}
              labelText=""
              // The visible name is the label, so the radio itself needs its own
              // accessible name rather than an empty one.
              aria-label={versionLabel(version)}
              checked={version.versionId === staged}
              onChange={() => setStaged(version.versionId)}
            />
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="md"
          disabled={!staged || staged === selectedId}
          onClick={() => staged && onApply(staged)}
        >
          Apply
        </Button>
      </div>
    </aside>
  );
};
